import type { FunctionComponent } from 'react';

import { Badge, Flex, Text } from '@mantine/core';

import { useTranslation } from 'i18n';

import type { ModifierGroupFormValue, PreviewModifierGroup } from 'helpers/menuPreview';
import { modifierGroupBadgeText } from 'helpers/menuPreview';
import { BADGE_VARIANTS } from 'constants/mantineVariants';

import { ModifierGroupFormItem } from 'components/MenuManagement/MenuPreview/ModifierGroupFormItem';
import { WarningIconSm } from '@chownow/pantry/icons';

const NS = 'modifierGroup';

type ModifierGroupProps = {
  modifierGroups: PreviewModifierGroup[];
  formModifiers: Record<string, ModifierGroupFormValue | undefined>;
  onModifierChange: (groupKey: string, value: ModifierGroupFormValue) => void;
  onDrillIn: (modifierKey: string, modifierName: string) => void;
  formErrors: { modifiers?: Record<string, string> };
  showErrors: boolean;
  hideLastDivider?: boolean;
};

export const ModifierGroup: FunctionComponent<ModifierGroupProps> = ({
  modifierGroups,
  formModifiers,
  onModifierChange,
  onDrillIn,
  formErrors,
  showErrors,
  hideLastDivider,
}) => {
  const { t } = useTranslation(NS);

  return (modifierGroups ?? []).map((modifierGroup, index) => {
    const groupError = showErrors ? formErrors?.modifiers?.[modifierGroup.groupKey] : null;
    const isLast = index === modifierGroups.length - 1;

    return (
      <div key={modifierGroup.groupKey}>
        <Flex justify="space-between" align="center" gap="sm" mb={groupError ? 'sm' : 'md'}>
          <Text variant="title-sm" miw={0}>
            {modifierGroup.name}
          </Text>
          <Badge
            variant={groupError ? BADGE_VARIANTS.caution : BADGE_VARIANTS.outline}
            size="xs"
            flex="0 0 auto"
            leftSection={
              groupError ? <WarningIconSm data-testid="modifier-group-caution-icon" /> : undefined
            }
          >
            {modifierGroupBadgeText(modifierGroup, t)}
          </Badge>
        </Flex>
        {groupError && (
          <Text c="var(--content-caution)" lh="xs" mb="sm">
            {groupError}
          </Text>
        )}
        <ModifierGroupFormItem
          onModifierChange={onModifierChange}
          onDrillIn={onDrillIn}
          groupValue={formModifiers[modifierGroup.groupKey]}
          showTrailingDivider={!(isLast && hideLastDivider)}
          {...modifierGroup}
        />
      </div>
    );
  });
};
